import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, ArrowUpRight, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Progress } from "@/components/ui/progress";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/enrollments").then(({ data }) => setEnrollments(data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  return (
    <div className="pt-32 md:pt-40 pb-28 min-h-screen" data-testid="dashboard-page">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 border-b border-gold-500/20 pb-10">
          <div>
            <Overline>My Academy</Overline>
            <h1 className="mt-4 font-serif text-5xl md:text-6xl text-white">Welcome, {user?.name?.split(" ")[0]}.</h1>
            <p className="mt-3 text-white/60">Continue where you left off and keep growing.</p>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 border border-gold-500/30 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-white/70 hover:border-gold-400 hover:text-gold-300 transition-colors">
            <LogOut size={14} /> Sign out
          </button>
        </Reveal>

        <div className="mt-14">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-serif text-3xl text-white">My Courses</h2>
            <Link to="/academy" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-500 hover:text-gold-300 transition-colors">
              Browse all <ArrowUpRight size={14} />
            </Link>
          </div>

          {loaded && enrollments.length === 0 ? (
            <div className="border border-gold-500/20 bg-ink-paper/40 p-14 text-center" data-testid="empty-enrollments">
              <BookOpen size={30} className="text-gold-500 mx-auto" strokeWidth={1.3} />
              <p className="mt-5 font-serif text-2xl text-white">You haven't enrolled yet.</p>
              <p className="mt-2 text-white/60 text-sm">Explore the Academy and start your first course today.</p>
              <Link to="/academy" className="mt-8 inline-flex items-center gap-2 bg-gold-500 px-7 py-3.5 text-sm font-semibold text-ink hover:bg-gold-400 transition-colors">
                Explore Courses
              </Link>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {enrollments.map((e, i) => (
                <Reveal key={e.course_slug} delay={(i % 3) * 0.06}>
                  <Link to={`/academy/${e.course_slug}`} data-testid={`enrolled-${e.course_slug}`} className="group flex h-full flex-col border border-gold-500/20 bg-ink-paper/40 hover:border-gold-400/60 transition-colors duration-500">
                    <div className="relative overflow-hidden aspect-[16/9]">
                      <img src={e.image} alt={e.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                    </div>
                    <div className="p-6 flex flex-1 flex-col">
                      <span className="text-[10px] uppercase tracking-[0.25em] text-gold-500">{e.category}</span>
                      <h3 className="mt-2 font-serif text-xl text-white">{e.title}</h3>
                      <div className="mt-auto pt-6">
                        <div className="flex justify-between text-xs text-white/55 mb-2">
                          <span>{e.progress}% complete</span>
                          <span>{e.completed}/{e.total}</span>
                        </div>
                        <Progress value={e.progress} className="h-1.5 bg-ink [&>div]:bg-gold-500" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
